import { GameState } from '../game/GameState';
import { DataRegistry } from './DataRegistry';
import { ArmyManager } from './ArmyManager';
import { DiplomacyManager } from './DiplomacyManager';
import { ProvinceManager } from './ProvinceManager';
import { RecruitmentManager } from './RecruitmentManager';
import { StrategicAIPlanner, type AIPlanState } from './StrategicAIPlanner';
import { WarManager } from './WarManager';

export class CampaignAI {
  static readonly instance = new CampaignAI();

  processMonth() {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    for (const faction of Object.values(campaign.factions)) {
      if (faction.id === campaign.playerFactionId) continue;
      const data = registry.factions[faction.id];
      if (!data) continue;
      const plan = StrategicAIPlanner.instance.ensurePlan(faction.id);
      faction.treasury += Math.max(2, 4 + Math.floor(faction.prestige / 18) + Math.floor(faction.legitimacy / 35));
      this.manageWar(faction.id, plan);
      if (campaign.turn % 2 === 0) this.manageEconomy(faction.id, plan);
      this.manageRecruitment(faction.id, plan);
      if (campaign.turn % 6 === 0) this.considerWar(faction.id, plan);
    }
  }

  private manageEconomy(factionId: string, plan: AIPlanState) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    const provinces = Object.values(campaign.provinces)
      .filter((province) => province.owner === factionId && province.controller === factionId)
      .sort((a, b) => a.foodSecurity - b.foodSecurity || b.corruption - a.corruption || a.prosperity - b.prosperity);
    if (!faction || !provinces.length || faction.treasury < Math.max(25, plan.reserveTarget * 0.12)) return;

    const stressed = provinces[0];
    const stressedData = DataRegistry.instance.provinces[stressed.id];
    if (stressed.corruption > 30 && faction.treasury >= 30) {
      ProvinceManager.instance.auditOfficials(stressed.id);
      return;
    }
    if (stressed.foodSecurity < 58 || plan.economicPriority === 'food') {
      const foodBuilding = stressedData?.port ? 'fishery' : 'rice_paddies';
      if (stressed.buildings.includes(foodBuilding)) ProvinceManager.instance.upgradeBuilding(stressed.id, foodBuilding);
      else ProvinceManager.instance.constructBuilding(stressed.id, foodBuilding);
      return;
    }
    if (!stressed.buildings.includes('granary') && faction.treasury >= 65) {
      ProvinceManager.instance.constructBuilding(stressed.id, 'granary');
      return;
    }

    if (plan.economicPriority === 'trade') {
      const port = provinces.find((province) => DataRegistry.instance.provinces[province.id]?.port && !province.buildings.includes('market'));
      if (port && faction.treasury >= 100) {
        ProvinceManager.instance.constructBuilding(port.id, 'market');
        return;
      }
      const warehouse = provinces.find((province) => DataRegistry.instance.provinces[province.id]?.port && !province.buildings.includes('warehouse'));
      if (warehouse && faction.treasury >= 90) {
        ProvinceManager.instance.constructBuilding(warehouse.id, 'warehouse');
        return;
      }
    }

    if (plan.economicPriority === 'infrastructure') {
      const road = provinces.find((province) => (province.roadLevel ?? 0) < 2);
      if (road && !road.buildings.includes('road_network') && faction.treasury >= 70) {
        ProvinceManager.instance.constructBuilding(road.id, 'road_network');
        return;
      }
    }

    const commercial = provinces.find((province) => !province.buildings.includes('market'));
    if (commercial && faction.treasury >= 100) {
      ProvinceManager.instance.constructBuilding(commercial.id, 'market');
      return;
    }

    const resourcePlan: Array<[string, string]> = [
      ['timber', 'logging_camp'], ['clay', 'clay_pit'], ['stone', 'stone_quarry'], ['iron', 'iron_mine'],
      ['spices', 'spice_terrace'], ['salt', 'salt_pans'],
    ];
    for (const province of provinces) {
      const deposits = DataRegistry.instance.provinces[province.id]?.deposits ?? [];
      const opportunity = resourcePlan.find(([deposit, building]) => deposits.includes(deposit) && !province.buildings.includes(building));
      if (opportunity && faction.treasury >= (DataRegistry.instance.buildings[opportunity[1]]?.costGold ?? 999)) {
        ProvinceManager.instance.constructBuilding(province.id, opportunity[1]);
        return;
      }
    }

    const upgrade = provinces
      .flatMap((province) => province.buildings.map((buildingId) => ({ province, buildingId })))
      .filter(({ buildingId }) => plan.economicPriority !== 'military' || ['fort', 'armory', 'powder_mill', 'shipyard'].includes(buildingId))
      .find(({ province, buildingId }) => {
        const building = DataRegistry.instance.buildings[buildingId];
        return (province.buildingLevels[buildingId] ?? 1) < (building?.maxLevel ?? 1)
          && faction.treasury >= ProvinceManager.instance.upgradeCost(buildingId, (province.buildingLevels[buildingId] ?? 1) + 1);
      });
    if (upgrade) ProvinceManager.instance.upgradeBuilding(upgrade.province.id, upgrade.buildingId);
  }

  private manageRecruitment(factionId: string, plan: AIPlanState) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const faction = campaign.factions[factionId];
    const data = registry.factions[factionId];
    const armies = Object.values(campaign.armies).filter((army) => army.factionId === factionId);
    const strength = armies.reduce((sum, army) => sum + ArmyManager.instance.armyStrength(army.id), 0);
    const provinces = Object.values(campaign.provinces).filter((province) => province.owner === factionId && province.controller === factionId);
    const desired = provinces.length * (plan.militaryPosture === 'aggressive' ? 900 : plan.militaryPosture === 'defensive' ? 650 : 760);
    if (!faction || !data || strength >= desired || faction.manpower <= plan.reserveTarget || faction.treasury < 25) return;
    if (campaign.recruitmentQueue.some((entry) => entry.factionId === factionId)) return;
    const province = provinces.filter((entry) => entry.foodSecurity > 42 && entry.population > 10).sort((a, b) => b.population + b.foodSecurity - a.population - a.foodSecurity)[0];
    if (!province) return;
    const preferredCategories = plan.militaryPosture === 'defensive' ? ['spear', 'ranged', 'infantry'] : plan.militaryPosture === 'aggressive' ? ['cavalry', 'artillery', 'infantry'] : ['infantry', 'ranged', 'spear'];
    const unitId = data.roster.find((id) => preferredCategories.some((category) => (registry.units[id]?.category ?? '').includes(category))) ?? data.roster[(campaign.turn + data.roster.length) % Math.max(1, data.roster.length)];
    const unit = unitId ? registry.units[unitId] : undefined;
    if (unitId && unit && faction.manpower > unit.manpower) RecruitmentManager.instance.recruit(factionId, province.id, unitId);
  }

  private manageWar(factionId: string, plan: AIPlanState) {
    const campaign = GameState.instance.requireCampaign();
    const wars = WarManager.instance.warsForFaction(factionId);
    if (!wars.length) return;
    const war = wars.sort((a, b) => Math.abs(b.warscore) - Math.abs(a.warscore))[0];
    const side = WarManager.instance.sideForFaction(war, factionId);
    const score = WarManager.instance.warScoreForFaction(war, factionId);
    const opponent = side === 'attacker' ? war.defenderLeader : war.attackerLeader;
    const isSideLeader = factionId === (side === 'attacker' ? war.attackerLeader : war.defenderLeader);
    if (score >= 42 && isSideLeader) {
      const term = side === 'attacker' && war.warGoalProvinceId && score >= 35 ? 'cede_war_goal' : 'war_reparations';
      if (DiplomacyManager.instance.makePeace(factionId, opponent, term)) return;
    }
    if ((score <= -48 || plan.strategy === 'survive') && campaign.turn - war.startTurn >= 7 && opponent !== campaign.playerFactionId) {
      if (DiplomacyManager.instance.makePeace(opponent, factionId, score <= -70 ? 'war_reparations' : 'white_peace')) return;
    }

    const armies = Object.values(campaign.armies)
      .filter((entry) => entry.factionId === factionId && entry.units.length && !entry.embarkedByFleetId)
      .sort((a, b) => ArmyManager.instance.armyStrength(b.id) - ArmyManager.instance.armyStrength(a.id));
    if (!armies.length) return;
    const target = WarManager.instance.targetForWar(war, factionId);
    if (!target) return;

    for (const [index, army] of armies.entries()) {
      const currentProvince = campaign.provinces[army.provinceId];
      const siege = WarManager.instance.siegeForProvince(army.provinceId);
      if (siege?.besiegerFactionId === factionId) continue;
      const enemies = ArmyManager.instance.defendersForProvince(army.provinceId, factionId);
      if (enemies.length) {
        if (plan.militaryPosture === 'defensive' && ArmyManager.instance.armyStrength(army.id) < 450) army.stance = 'defend';
        else ArmyManager.instance.resolveCampaignSkirmish(army.id);
        continue;
      }
      if (currentProvince && currentProvince.owner !== factionId && WarManager.instance.areEnemies(factionId, currentProvince.owner)) {
        if ((currentProvince.fortLevel ?? 0) > 0 && currentProvince.controller !== factionId) WarManager.instance.beginSiege(army.id);
        else if (currentProvince.controller !== factionId) WarManager.instance.occupyProvince(currentProvince.id, factionId);
        else if (campaign.turn % 6 === 0 && plan.riskTolerance > 55) WarManager.instance.lootProvince(army.id);
        continue;
      }
      if (!army.destinationProvinceId && (index < 2 || plan.militaryPosture === 'aggressive')) ArmyManager.instance.moveArmy(army.id, target.id);
    }
  }

  private considerWar(factionId: string, plan: AIPlanState) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[factionId];
    if (!faction || plan.militaryPosture !== 'aggressive' || plan.confidence < 55 || plan.riskTolerance < 45) return;
    if (WarManager.instance.warsForFaction(factionId).length || faction.warExhaustion > 30 || faction.manpower < Math.max(plan.reserveTarget, faction.maxManpower * 0.28)) return;
    const targetProvince = plan.targetProvinceId ? campaign.provinces[plan.targetProvinceId] : undefined;
    const targetFactionId = plan.targetFactionId ?? targetProvince?.owner;
    if (!targetProvince || !targetFactionId || targetFactionId === campaign.playerFactionId && plan.confidence < 70) return;
    if (faction.treaties.alliances.includes(targetFactionId) || faction.treaties.nonAggression.includes(targetFactionId) || (faction.truceUntil[targetFactionId] ?? 0) > campaign.turn) return;
    if (DiplomacyManager.instance.opinion(factionId, targetFactionId) > -5 && !faction.claims.includes(targetProvince.id)) return;
    const options = WarManager.instance.availableCasusBelli(factionId, targetFactionId);
    const option = options.find((entry) => entry.warGoalProvinceId === targetProvince.id) ?? options[0];
    if (option) DiplomacyManager.instance.declareWar(factionId, targetFactionId, option.id, option.warGoalProvinceId);
  }
}
